import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IslandApiResponse } from 'state/data/slice'
import { FilterSelectionMode, getFilterSelectionMode } from 'state/filterTypes'
import { allFiltersWithoutFunction } from 'state/kodiakIslands/Filters'
import { MemoryFilter } from 'state/kodiakIslands/types'

import { IslandOrderBy, OrderDirection } from './types'

export type TransitionType = 'sort' | 'filter' | 'search' | null

export interface KodiakIslandsState {
  kodiakIslands: IslandApiResponse[]
  isLoading: boolean

  searchTerm: string
  orderBy: IslandOrderBy
  orderDirection: OrderDirection
  showUserPositionsOnly: boolean
  hasActiveRewards: boolean
  filters: MemoryFilter[]

  offset: number
  limit: number

  dataTransition: boolean
  transitionType: TransitionType
}

export const initialState: KodiakIslandsState = {
  kodiakIslands: [],
  isLoading: false,

  searchTerm: '',
  orderBy: IslandOrderBy.AprAverageApr,
  orderDirection: OrderDirection.Desc,
  showUserPositionsOnly: false,
  hasActiveRewards: false,
  filters: allFiltersWithoutFunction,

  offset: 0,
  limit: 20,

  dataTransition: false,
  transitionType: null,
}

const kodiakIslands = createSlice({
  name: 'kodiakIslands',
  initialState,
  reducers: {
    updateKodiakIslands(state, { payload: { kodiakIslands } }: PayloadAction<{ kodiakIslands: IslandApiResponse[] }>) {
      if (Array.isArray(kodiakIslands)) {
        state.kodiakIslands = kodiakIslands.filter(Boolean)
        state.isLoading = false
        state.dataTransition = false
        state.transitionType = null
      }
    },
    clearKodiakIslands(state) {
      state.kodiakIslands = []
      state.isLoading = true
    },

    setSearchTerm(state, { payload }: PayloadAction<string>) {
      const next = payload ?? ''
      if (state.searchTerm !== next) {
        state.searchTerm = next
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'search'
        state.isLoading = true
      }
    },
    setSortBy(
      state,
      {
        payload: { orderBy, orderDirection },
      }: PayloadAction<{ orderBy: IslandOrderBy; orderDirection: OrderDirection }>
    ) {
      const sortChanged = orderBy !== state.orderBy || orderDirection !== state.orderDirection
      state.orderBy = orderBy
      state.orderDirection = orderDirection
      if (sortChanged) {
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'sort'
        state.isLoading = true
      }
    },
    toggleUserPositions(state) {
      state.showUserPositionsOnly = !state.showUserPositionsOnly
      state.offset = 0
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
    },
    toggleActiveRewards(state) {
      state.hasActiveRewards = !state.hasActiveRewards
      state.offset = 0
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
    },

    toggleFilter: (state, action: PayloadAction<string>) => {
      const filterDisplayName = action.payload
      const filter = state.filters.find((f) => f.displayName === filterDisplayName)

      if (filter) {
        const selectionMode = getFilterSelectionMode(filter)

        if (selectionMode === FilterSelectionMode.SINGLE) {
          if (filter.isActive) {
            state.filters = state.filters.map((f) => {
              if (f.group === filter.group) {
                return { ...f, isActive: false }
              }
              return f
            })
          } else {
            state.filters = state.filters.map((f) => {
              if (f.group === filter.group) {
                return { ...f, isActive: f.displayName === filterDisplayName }
              }
              return f
            })
          }
        } else {
          filter.isActive = !filter.isActive
        }

        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'filter'
        state.isLoading = true
      }
    },

    activateFilter(state, { payload: filter }: PayloadAction<MemoryFilter>) {
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
      state.offset = 0

      if (filter.group) {
        state.filters = state.filters.map((f) =>
          f.group === filter.group ? { ...f, isActive: f.displayName === filter.displayName } : f
        )
      } else {
        state.filters = state.filters.map((f) => (f.displayName === filter.displayName ? { ...f, isActive: true } : f))
      }
    },
    deactivateFilter(state, { payload: filter }: PayloadAction<MemoryFilter>) {
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
      state.offset = 0

      state.filters = state.filters.map((f) => (f.displayName === filter.displayName ? { ...f, isActive: false } : f))
    },
    resetFilterState(state) {
      state.searchTerm = ''
      state.orderBy = IslandOrderBy.AprAverageApr
      state.orderDirection = OrderDirection.Desc
      state.showUserPositionsOnly = false
      state.hasActiveRewards = false
      state.filters = allFiltersWithoutFunction
      state.offset = 0
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
    },

    setPagination(state, { payload: { offset, limit } }: PayloadAction<{ offset?: number; limit?: number }>) {
      const onlyPagingChanged =
        (typeof offset === 'number' && offset !== state.offset) || (typeof limit === 'number' && limit !== state.limit)

      if (typeof offset === 'number') state.offset = offset
      if (typeof limit === 'number') state.limit = limit

      if (onlyPagingChanged) {
        state.dataTransition = true
        state.transitionType = 'filter'
        state.isLoading = true
      }
    },

    updateQueryParams(
      state,
      {
        payload: { orderBy, orderDirection, offset, limit },
      }: PayloadAction<{ orderBy?: IslandOrderBy; orderDirection?: OrderDirection; offset?: number; limit?: number }>
    ) {
      const sortWillChange =
        (orderBy !== undefined && orderBy !== state.orderBy) ||
        (orderDirection !== undefined && orderDirection !== state.orderDirection)

      if (orderBy !== undefined) state.orderBy = orderBy
      if (orderDirection !== undefined) state.orderDirection = orderDirection

      if (sortWillChange) {
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'sort'
        state.isLoading = true
      }

      if (limit !== undefined) state.limit = limit
      if (offset !== undefined) state.offset = offset

      if (!sortWillChange && (limit !== undefined || offset !== undefined)) {
        state.dataTransition = true
        state.transitionType = 'filter'
        state.isLoading = true
      }
    },

    setIslandsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload
    },
    setDataTransition(
      state,
      { payload: { inTransition, type } }: PayloadAction<{ inTransition: boolean; type: TransitionType }>
    ) {
      state.dataTransition = inTransition
      state.transitionType = inTransition ? type : null
      if (inTransition) state.isLoading = true
    },
    endDataTransition(state) {
      state.dataTransition = false
      state.transitionType = null
    },
  },
})

export const {
  updateKodiakIslands,
  clearKodiakIslands,
  setSearchTerm,
  setSortBy,
  toggleUserPositions,
  toggleActiveRewards,
  toggleFilter,
  activateFilter,
  deactivateFilter,
  resetFilterState,
  setPagination,
  updateQueryParams,
  setIslandsLoading,
  setDataTransition,
  endDataTransition,
} = kodiakIslands.actions

export default kodiakIslands.reducer
